import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatDate, useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Spinner, Table, TableRow, Typography } from '@chilecompra/react-kit/components'
import { PUBLIC_URL } from '../../config/settings/environment'
import { ANNUAL_PURCHASE_PLAN_TABS, GESTION_TITLE } from '../../config/settings/constant'
import { formatAmount } from '../../modules/utils/formatters'
import { calculateDefaultYearAndMonth, setStringMonth } from '../BehaviorSection/BehaviorSection.utils'
import DimensionsChart from '../BehaviorSection/BehaviorSection.DimensionsChart'
import { onGetPDFdataThunk, onDownloadPdfThunk } from './ToDownload.actions'
import ScrollToTopButton from './ToDownloadScroll.Button'
import {
  Box,
  Card,
  CardItem,
  Column,
  Container,
  ContainerRef,
  CustomCard,
  Item,
  Logo,
  NotFoundImg,
  TableWrapper,
  Row,
  Separator,
  TableCell,
  WarningImg
} from './ToDownload.styles'

/**
 * Main container to download.
 */

const ContainerToDownload = () => {
  const dni = usePathParam('rut')
  const dispatch = useDispatch()
  const {
    size: { isTiny, isDownToSmall, isDownToMedium }
  } = useViewport()
  const { year, stringMonth } = calculateDefaultYearAndMonth()

  const { orgLegalName, orgAddress, email, phone, webSite } = useSelector(state => state.basicBuyerInformation)
  const { rangeDate, totalPurchaseAmount } = useSelector(state => state.totalPurchases)
  const {
    error: purchaseOrdersError,
    purchaseOrders,
    rangeDate: rangeDateOC,
    totalOrder
  } = useSelector(state => state.totalPurchaseOrders)
  const {
    error: claimError,
    claimsIrregularity,
    paymentNotTime,
    totalClaim,
    rangeDate: rangeDateClaim
  } = useSelector(state => state.claimInformation)
  const { days, sigfe, rangeDate: rangeDateAvg } = useSelector(state => state.averagePay)
  const {
    error: institutionalError,
    representativeList = [],
    administratorList = []
  } = useSelector(state => state.institutionalManagement)
  const { error: dependentBuyerError, dependentBuyer = [] } = useSelector(state => state.dependentBuyer)
  const { error: annualPurchasePlanError, gridData, publicationDate } = useSelector(state => state.annualPurchasePlan)
  const {
    error: publicProcurementCourtClaimsError,
    initialDate,
    finalDate,
    totalDemands
  } = useSelector(state => state.publicProcurementCourtClaims)
  const {
    averageBehaviorIndicatorLoading: loadingBehavior,
    averageBehaviorIndicatorError: errorBehavior,
    averageBehaviorIndicator: indicator,
    dimensions,
    dimensionsError,
    updatedAt,
    averageBehaviorYear,
    averageBehaviorMonth
  } = useSelector(state => state.behaviorSection)

  const { loading } = useSelector(state => state.pdfData)
  const hasClaims = totalClaim >= 0 && totalOrder > 0

  const averageBehaviorStringMonth = setStringMonth(averageBehaviorYear, averageBehaviorMonth)

  useEffectOnce(() => {
    dispatch(onGetPDFdataThunk(dni))
  }, [])

  const getCurrentDate = new Date()

  const gettingMonthAndYear = date => {
    const aux = new Date(date)
    const optionMonth = { month: 'long' }
    const month = aux.toLocaleString('es-ES', optionMonth)
    const year = aux.getFullYear()
    const result = { month, year }
    return result
  }

  const getFormattedDate = () => {
    const date = new Date()
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
    return new Intl.DateTimeFormat('es-ES', options).format(date).replace(' ', ' de ').replace(',', ' a las')
  }

  const contentRef = useRef(null)

  useEffect(() => {
    const contentContainer = contentRef.current
    if (contentContainer && !loading) {
      setTimeout(() => {
        // wait for the content to be fully rendered
        dispatch(onDownloadPdfThunk({ contentContainer, dni }))
      }, 1000)
    }
  }, [loading])

  return (
    <ContainerRef ref={contentRef} width={isDownToSmall ? '620px' : '900px'}>
      <>
        {!loading && (
          <Container id="container" direction="row" padding={isDownToMedium ? '20px' : '40px'}>
            <Row md={12}>
              <Item width="auto">
                <Logo alt="Logo Chile compra" src={`${PUBLIC_URL}/assets/images/logo_mercadopublico.png`} />
              </Item>
            </Row>
            <Row justifyContent="center" md={12} flexDirection="column" alignItems="center">
              <Typography variant="h2" fontWeight="bold">
                Ficha del Comprador
              </Typography>
              <Typography padding="8px 0 0 0" variant="subtitle">
                Documento generado el {getFormattedDate()}
              </Typography>
            </Row>
            <Row justifyContent="start" padding="48px 0 0 0">
              <Column md={8} sm={12}>
                <Card padding="16px 20px 8px 0">
                  <Table accessibility={{ label: 'Información de la institución' }}>
                    <TableRow>
                      <TableCell enableHead width="120px" tabIndex="0">
                        Razón Social
                      </TableCell>
                      <TableCell tabIndex="0">{orgLegalName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell enableHead width="120px" tabIndex="0">
                        RUT
                      </TableCell>
                      <TableCell tabIndex="0">{dni}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell enableHead width="120px" tabIndex="0">
                        Teléfono
                      </TableCell>
                      <TableCell tabIndex="0">{phone}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell enableHead width="120px" tabIndex="0">
                        Sitio web
                      </TableCell>
                      <TableCell tabIndex="0">{webSite}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell enableHead width="120px" tabIndex="0">
                        Correo electrónico
                      </TableCell>
                      <TableCell tabIndex="0">{email}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell disableBottomBorder enableHead width="120px" tabIndex="0">
                        Domicilio legal
                      </TableCell>
                      <TableCell disableBottomBorder tabIndex="0">
                        {orgAddress}
                      </TableCell>
                    </TableRow>
                  </Table>
                </Card>
              </Column>
              <Column justifyContent="center" md={4} sm={12}>
                <CustomCard padding="14px 16px">
                  <Box>
                    <Typography
                      color="black1"
                      align="left"
                      variant="caption"
                      fontSize="14px"
                      lineHeight="16px"
                      tabIndex="0"
                    >
                      Compras en Mercado Público
                    </Typography>
                    <Typography
                      align="left"
                      fontWeight="bold"
                      variant="body1"
                      fontSize="18px"
                      color="black1"
                      lineHeight="31px"
                      tabIndex="0"
                    >
                      {totalPurchaseAmount === -1 ? 'Sin información' : '$ ' + formatAmount(totalPurchaseAmount)}
                    </Typography>
                    <Typography
                      align="left"
                      fontWeight="regular"
                      variant="subtitle"
                      fontSize="12px"
                      lineHeight="18px"
                      tabIndex="0"
                    >
                      {totalPurchaseAmount === -1
                        ? `No ha realizado compras entre ${rangeDate.initialMonth} ${rangeDate.initialYear} y ${rangeDate.finalMonth} ${rangeDate.finalYear}`
                        : `Periodo consultado: ${rangeDate.initialMonth} ${rangeDate.initialYear} a
              ${rangeDate.finalMonth} ${rangeDate.finalYear}. Impuestos incluidos.`}
                    </Typography>
                  </Box>
                  <Separator />
                  {!errorBehavior && !loadingBehavior && (
                    <Box>
                      <Typography
                        color="black1"
                        align="left"
                        variant="caption"
                        fontSize="14px"
                        lineHeight="16px"
                        tabIndex="0"
                      >
                        {GESTION_TITLE}
                      </Typography>
                      <Typography
                        align="left"
                        fontWeight="bold"
                        variant="body1"
                        fontSize="18px"
                        color="black1"
                        lineHeight="31px"
                        tabIndex="0"
                      >
                        {indicator?.toFixed(2).replace('.', ',') || 0} /5
                      </Typography>
                      <Typography
                        align="left"
                        fontWeight="regular"
                        variant="subtitle"
                        fontSize="12px"
                        lineHeight="18px"
                        tabIndex="0"
                      >
                        Nota calculada en base al puntaje de las cinco dimensiones de Gestión de compras durante el mes
                        de {averageBehaviorStringMonth || stringMonth} {averageBehaviorYear || year}.
                      </Typography>
                    </Box>
                  )}
                  <Separator />
                  <Box>
                    <Typography
                      color="black1"
                      align="left"
                      variant="caption"
                      fontSize="14px"
                      lineHeight="16px"
                      tabIndex="0"
                    >
                      Reclamos
                    </Typography>
                    <Typography
                      align="left"
                      fontWeight="bold"
                      variant="body1"
                      fontSize="18px"
                      color="black1"
                      lineHeight="31px"
                      tabIndex="0"
                    >
                      {!hasClaims ? 'Sin información' : formatAmount(totalClaim) + ' reclamos'}
                    </Typography>
                    <Typography
                      align="left"
                      fontWeight="regular"
                      variant="subtitle"
                      fontSize="12px"
                      lineHeight="18px"
                      tabIndex="0"
                    >
                      {!hasClaims
                        ? ` Sin información entre ${rangeDateClaim.initialMonth} ${rangeDateClaim.initialYear} y ${rangeDateClaim.finalMonth} ${rangeDateClaim.finalYear}`
                        : ` de ${formatAmount(totalOrder)} órdenes de compra emitidas entre ${rangeDateClaim.initialMonth} 
                ${rangeDateClaim.initialYear} a
              ${rangeDateClaim.finalMonth} ${rangeDateClaim.finalYear}`}
                    </Typography>
                  </Box>
                  <Separator />
                  <Box>
                    <Typography
                      color="black1"
                      align="left"
                      variant="caption"
                      fontSize="14px"
                      lineHeight="16px"
                      tabIndex="0"
                    >
                      Promedio días de pago
                    </Typography>
                    <Typography
                      align="left"
                      fontWeight="bold"
                      variant="body1"
                      fontSize="18px"
                      color="black1"
                      lineHeight="31px"
                      tabIndex="0"
                    >
                      {days === -1 || sigfe === false ? 'Sin información' : `${days} días`}
                    </Typography>
                    <Typography
                      align="left"
                      fontWeight="regular"
                      variant="subtitle"
                      fontSize="12px"
                      lineHeight="18px"
                      tabIndex="0"
                    >
                      {sigfe === false ? (
                        `Este organismo comprador no utiliza el Sistema de Información para la Gestión Financiera del Estado (SIGFE)`
                      ) : (
                        <>
                          Entre {rangeDateAvg.initialMonth} {rangeDateAvg.initialYear} y {rangeDateAvg.finalMonth}{' '}
                          {rangeDateAvg.finalYear}.
                          <br />
                          Información obtenida desde el Sistema de Información para la Gestión Financiera del Estado
                          (SIGFE).
                        </>
                      )}
                    </Typography>
                  </Box>
                </CustomCard>
              </Column>
            </Row>
            <Row padding="40px 0 0 0" flexDirection="column" maxWidth={isDownToMedium ? 'auto' : '66%'}>
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                Información General
              </Typography>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                padding="11px 0 0 0"
                variant="h4"
                lineHeight="22px"
                tabIndex="0"
              >
                Administración de la institución
              </Typography>

              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                padding="11px 0 0 0"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                Fuente: Información ingresada por el organismo público en Mercado Público.
              </Typography>
            </Row>
            {!institutionalError && (
              <Row padding="19px 0 6px 0">
                <Typography
                  color="black1"
                  fontSize="18px"
                  fontWeight="bold"
                  variant="h4"
                  lineHeight="22px"
                  tabIndex="0"
                >
                  Personas con rol administrador en Mercado Público
                </Typography>
              </Row>
            )}
            {institutionalError && (
              <Card
                display="flex"
                enableBorder
                color="blank"
                margin="43px 0 44px 0"
                alignContent="center"
                height={isTiny ? '139px' : '243px'}
              >
                <CardItem direction="row" padding="25px 16px">
                  <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
                  <Typography
                    color="black1"
                    variant="body1"
                    fontWeight="regular"
                    fontSize="16px"
                    lineHeight="21px"
                    margin="0 0 0 33px"
                    tabIndex="0"
                    sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
                  >
                    No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                    intentar dentro de unos minutos.
                  </Typography>
                </CardItem>
              </Card>
            )}
            {!institutionalError && administratorList.length > 0 && (
              <Row accessibility={{ label: 'Rol Administrador' }} padding="6px 0 0 0" flexDirection="column">
                {administratorList.map((authorities, index) => {
                  return (
                    <Typography
                      key={index}
                      fontWeight="regular"
                      variant="body1"
                      fontSize="14px"
                      color="black1"
                      lineHeight="18px"
                      tabIndex="0"
                    >
                      {authorities.firstName + ' ' + authorities.lastName}
                    </Typography>
                  )
                })}
              </Row>
            )}
            {!institutionalError && administratorList.length === 0 && (
              <Row accessibility={{ label: 'Rol Administrador' }} padding="0 0 0 19px" flexDirection="column">
                <Typography
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Sin información
                </Typography>
              </Row>
            )}
            {!institutionalError && (
              <Row padding="30px 0 6px 0">
                <Typography
                  color="black1"
                  fontSize="18px"
                  fontWeight="bold"
                  variant="h4"
                  lineHeight="22px"
                  tabIndex="0"
                >
                  Autoridad representante
                </Typography>
              </Row>
            )}
            {!institutionalError && representativeList.length > 0 && (
              <Row accessibility={{ label: 'Rol Administrador' }} padding="6px 0" flexDirection="column">
                {representativeList.map((authorities, index) => {
                  return (
                    <Typography
                      key={index}
                      fontWeight="regular"
                      variant="body1"
                      fontSize="14px"
                      color="black1"
                      lineHeight="18px"
                      tabIndex="0"
                    >
                      {authorities.firstName + ' ' + authorities.lastName}
                    </Typography>
                  )
                })}
              </Row>
            )}
            {!institutionalError && representativeList.length === 0 && (
              <Row accessibility={{ label: 'Rol Administrador' }} padding="0 0 0 19px" flexDirection="column">
                <Typography
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Sin información
                </Typography>
              </Row>
            )}
            <Row flexDirection="column" padding="19px 0" maxWidth={isDownToMedium ? 'auto' : '66%'}>
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                Organismos compradores dependientes
              </Typography>
              <Typography
                fontWeight="regular"
                variant="body1"
                fontSize="14px"
                padding="11px 0 0 0"
                color="black1"
                lineHeight="18px"
                tabIndex="0"
              >
                Son aquellas unidades de compra internas de una institución que operan bajo un RUT distinto al del
                organismo del cual depende.
              </Typography>
            </Row>
            {dependentBuyerError && (
              <Card
                display="flex"
                enableBorder
                color="blank"
                margin="43px 0 44px 0"
                alignContent="center"
                height={isTiny ? '139px' : '243px'}
              >
                <CardItem direction="row" padding="25px 16px">
                  <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
                  <Typography
                    color="black1"
                    variant="body1"
                    fontWeight="regular"
                    fontSize="16px"
                    lineHeight="21px"
                    margin="0 0 0 33px"
                    tabIndex="0"
                    sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
                  >
                    No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                    intentar dentro de unos minutos.
                  </Typography>
                </CardItem>
              </Card>
            )}
            {!dependentBuyerError && dependentBuyer.length > 0 && (
              <Row padding="0" display="flex" color="blank" maxWidth={isDownToMedium ? 'auto' : '89%'}>
                <Table padding="19px 0">
                  <TableRow>
                    <TableCell enableHead tabIndex="0">
                      Razón Social
                    </TableCell>
                    <TableCell enableHead tabIndex="0">
                      RUT
                    </TableCell>
                  </TableRow>
                  {dependentBuyer.map((buyers, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell tabIndex="0">{buyers.orgLegalName}</TableCell>
                        <TableCell tabIndex="0">{buyers.dni}</TableCell>
                      </TableRow>
                    )
                  })}
                </Table>
              </Row>
            )}
            {!dependentBuyerError && dependentBuyer.length === 0 && (
              <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
                <CardItem direction="column">
                  <NotFoundImg
                    alt="No se encontraron resultados"
                    margin="52px 0 0 0"
                    src={`${PUBLIC_URL}/assets/images/notFound.svg`}
                  />
                  <Typography
                    color="black1"
                    variant="h4"
                    fontWeight="bold"
                    fontSize="18px"
                    lineHeight="22px"
                    margin="10px 0 0 0"
                    tabIndex="0"
                  >
                    {ANNUAL_PURCHASE_PLAN_TABS.NO_INFO}
                  </Typography>
                  <Typography
                    color="black3"
                    variant="body2"
                    fontWeight="regular"
                    fontSize="14px"
                    lineHeight="18px"
                    margin="5px 0 52px 0"
                    tabIndex="0"
                    sx={{ maxWidth: '270px', textAlign: 'center' }}
                  >
                    El organismo comprador no cuenta con organismos compradores dependientes.
                  </Typography>
                </CardItem>
              </Card>
            )}
            <Row padding="19px 0" flexDirection="column" maxWidth={isDownToMedium ? 'auto' : '66%'}>
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                Plan anual de compras
              </Typography>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                padding="11px 0 0 0"
                variant="h4"
                lineHeight="22px"
                tabIndex="0"
              >
                Ejecución del plan anual de compras
              </Typography>

              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                padding="11px 0 0 0"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                {!annualPurchasePlanError
                  ? `Fuente: Plan anual de compras publicado por la institución en Mercado Público el ${publicationDate}.`
                  : 'Fuente: Plan anual de compras publicado por la institución en Mercado Público'}
              </Typography>
            </Row>
            {!annualPurchasePlanError && (
              <Row padding="19px 0" maxWidth={isDownToMedium ? 'auto' : '66%'}>
                <Typography
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Revisa la ejecución de los montos planificados durante cada mes del año. Esta información se obtiene a
                  través de las órdenes de compra que sean enlazadas a los distintos ítems que componen los proyectos
                  del plan anual de compras.
                </Typography>
              </Row>
            )}
            {annualPurchasePlanError && (
              <Card
                display="flex"
                enableBorder
                color="blank"
                margin="43px 0 44px 0"
                alignContent="center"
                height={isTiny ? '139px' : '243px'}
              >
                <CardItem direction="row" padding="25px 16px">
                  <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
                  <Typography
                    color="black1"
                    variant="body1"
                    fontWeight="regular"
                    fontSize="16px"
                    lineHeight="21px"
                    margin="0 0 0 33px"
                    tabIndex="0"
                    sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
                  >
                    No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                    intentar dentro de unos minutos.
                  </Typography>
                </CardItem>
              </Card>
            )}
            <Row padding="20px 0" maxWidth={isDownToMedium ? 'auto' : '89%'}>
              {!annualPurchasePlanError && gridData.length === 0 && (
                <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
                  <CardItem direction="column">
                    <NotFoundImg
                      alt="No se encontraron resultados"
                      margin="52px 0 0 0"
                      src={`${PUBLIC_URL}/assets/images/notFound.svg`}
                    />
                    <Typography
                      color="black1"
                      variant="h4"
                      fontWeight="bold"
                      fontSize="18px"
                      lineHeight="22px"
                      margin="10px 0 0 0"
                      tabIndex="0"
                    >
                      {ANNUAL_PURCHASE_PLAN_TABS.NO_INFO}
                    </Typography>
                    <Typography
                      color="black3"
                      variant="body2"
                      fontWeight="regular"
                      fontSize="14px"
                      lineHeight="18px"
                      margin="5px 0 52px 0"
                      tabIndex="0"
                      sx={{ maxWidth: '270px', textAlign: 'center' }}
                    >
                      {ANNUAL_PURCHASE_PLAN_TABS.ANNUAL_PURCHASE_PLAN_EXECUTION.NO_DATA}
                    </Typography>
                  </CardItem>
                </Card>
              )}
              {!annualPurchasePlanError && gridData.length > 0 && (
                <TableWrapper>
                  <Card padding="0" display="flex" color="blank" enableBorder width="100%">
                    <Table>
                      <TableRow>
                        <TableCell enableHead tabIndex="0">
                          Año
                        </TableCell>
                        <TableCell enableHead tabIndex="0">
                          Mes
                        </TableCell>
                        <TableCell enableHead tabIndex="0">
                          Monto planificado para el mes
                        </TableCell>
                        <TableCell enableHead tabIndex="0">
                          Monto ejecutado en el mes
                        </TableCell>
                        <TableCell enableHead tabIndex="0">
                          Porcentaje de ejecución
                        </TableCell>
                      </TableRow>
                      {gridData.map((item, index) => {
                        const isLastItem = index === gridData.length - 1
                        return (
                          <TableRow key={index}>
                            <TableCell tabIndex="0" disableBottomBorder={isLastItem}>
                              {item?.year}
                            </TableCell>
                            <TableCell tabIndex="0" disableBottomBorder={isLastItem}>
                              {item?.month}
                            </TableCell>
                            <TableCell tabIndex="0" textAlign="end" disableBottomBorder={isLastItem}>
                              {item?.plannedAmount}
                            </TableCell>
                            <TableCell tabIndex="0" textAlign="end" disableBottomBorder={isLastItem}>
                              {item?.executedAmount}
                            </TableCell>
                            <TableCell tabIndex="0" textAlign="end" disableBottomBorder={isLastItem}>
                              {item?.percent}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </Table>
                  </Card>
                </TableWrapper>
              )}
            </Row>
            <Row padding="20px 0" flexDirection="column">
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                Órdenes de compra
              </Typography>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                padding="11px 0 0 0"
                variant="h4"
                lineHeight="22px"
                tabIndex="0"
              >
                Resumen de sus órdenes de compra
              </Typography>

              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                padding="11px 0 0 0"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                Fuente: Órdenes de compra del organismo público en Mercado Público.
              </Typography>
            </Row>
            {!purchaseOrdersError && (
              <Row padding="19px 0" maxWidth={isDownToMedium ? 'auto' : '66%'}>
                <Typography
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Cifras generales de las órdenes de compra generadas por la institución durante el periodo de{' '}
                  {rangeDateOC?.initialtMonth} {rangeDateOC?.initialYear} a {rangeDateOC?.finalMonth}{' '}
                  {rangeDateOC?.finalYear}.
                </Typography>
              </Row>
            )}
            {purchaseOrdersError && (
              <Card
                display="flex"
                enableBorder
                color="blank"
                margin="43px 0 44px 0"
                alignContent="center"
                height={isTiny ? '139px' : '243px'}
              >
                <CardItem direction="row" padding="25px 16px">
                  <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
                  <Typography
                    color="black1"
                    variant="body1"
                    fontWeight="regular"
                    fontSize="16px"
                    lineHeight="21px"
                    margin="0 0 0 33px"
                    tabIndex="0"
                    sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
                  >
                    No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                    intentar dentro de unos minutos.
                  </Typography>
                </CardItem>
              </Card>
            )}
            <Row padding="20px 0" maxWidth={isDownToMedium ? 'auto' : '89%'}>
              {!purchaseOrdersError && purchaseOrders.length > 0 && (
                <Card padding="10px" display="flex" color="blank" enableBorder>
                  <Row flexWrap="wrap" padding="10px">
                    <Column display="flex" padding="0 15px 0 0">
                      <Typography
                        color="black1"
                        fontSize="18px"
                        fontWeight="bold"
                        padding="5px 0"
                        variant="h4"
                        lineHeight="28px"
                      >
                        {totalOrder}
                      </Typography>
                    </Column>
                    <Column>
                      <Row>
                        <Typography color="black1" fontSize="14px" variant="h4" lineHeight="28px">
                          Órdenes de compra generadas
                        </Typography>
                      </Row>
                    </Column>
                  </Row>
                  <Separator />
                  {purchaseOrders.map((item, index) => {
                    const isLastItem = index === purchaseOrders.length - 1
                    return (
                      <React.Fragment key={index}>
                        <Row key={index} flexWrap="nowrap" padding="10px">
                          <Column display="flex" padding="0 15px 0 0">
                            <Typography
                              color="black1"
                              fontSize="18px"
                              fontWeight="bold"
                              padding="5px 0"
                              variant="h4"
                              lineHeight="28px"
                            >
                              {item?.totalOC}
                            </Typography>
                          </Column>
                          <Column display="flex">
                            <Typography color="black1" fontSize="14px" padding="5px 0" variant="h4" lineHeight="28px">
                              {`Órdenes de compra generadas en ${item?.name}`}
                            </Typography>
                          </Column>
                        </Row>
                        {!isLastItem && <Separator />}
                      </React.Fragment>
                    )
                  })}
                </Card>
              )}
              {!purchaseOrdersError && purchaseOrders.length === 0 && (
                <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
                  <CardItem direction="column">
                    <NotFoundImg
                      alt="No se encontraron resultados"
                      margin="52px 0 0 0"
                      src={`${PUBLIC_URL}/assets/images/notFound.svg`}
                    />
                    <Typography
                      color="black1"
                      variant="h4"
                      fontWeight="bold"
                      fontSize="18px"
                      lineHeight="22px"
                      margin="10px 0 0 0"
                      tabIndex="0"
                    >
                      {ANNUAL_PURCHASE_PLAN_TABS.NO_INFO}
                    </Typography>
                    <Typography
                      color="black3"
                      variant="body2"
                      fontWeight="regular"
                      fontSize="14px"
                      lineHeight="18px"
                      margin="5px 0 52px 0"
                      tabIndex="0"
                      sx={{ maxWidth: '270px', textAlign: 'center' }}
                    >
                      El organismo comprador no ha emitido órdenes de compra durante los últimos 12 meses.
                    </Typography>
                  </CardItem>
                </Card>
              )}
            </Row>
            <Row padding="19px 0" flexDirection="column">
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                Reclamos
              </Typography>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                padding="11px 0 0 0"
                variant="h4"
                lineHeight="22px"
                tabIndex="0"
              >
                Reclamos recibidos
              </Typography>

              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                padding="11px 0 0 0"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                Fuente: Reclamos ingresados a través del Centro de Ayuda de Mercado Público.
              </Typography>
            </Row>
            {!claimError && (
              <Row padding="19px 0" maxWidth={isDownToMedium ? 'auto' : '66%'}>
                <Typography
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Número de reclamos que ha recibido el organismo comprador en base a todos sus procesos de compra
                  durante el periodo de {rangeDateClaim.initialMonth} {rangeDateClaim.initialYear} a{' '}
                  {rangeDateClaim.finalMonth} {rangeDateClaim.finalYear}.
                </Typography>
              </Row>
            )}
            {claimError && (
              <Card
                display="flex"
                enableBorder
                color="blank"
                margin="43px 0 44px 0"
                alignContent="center"
                height={isTiny ? '139px' : '243px'}
              >
                <CardItem direction="row" padding="25px 16px">
                  <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
                  <Typography
                    color="black1"
                    variant="body1"
                    fontWeight="regular"
                    fontSize="16px"
                    lineHeight="21px"
                    margin="0 0 0 33px"
                    tabIndex="0"
                    sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
                  >
                    No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                    intentar dentro de unos minutos.
                  </Typography>
                </CardItem>
              </Card>
            )}
            <Row padding="19px 0" maxWidth={isDownToMedium ? 'auto' : '89%'}>
              {!claimError && (claimsIrregularity > 0 || paymentNotTime > 0) && (
                <Card padding="10px" display="flex" color="blank" enableBorder>
                  <Row flexWrap="wrap" padding="10px">
                    <Column display="flex" padding="0 15px 0 0">
                      <Typography
                        color="black1"
                        fontSize="18px"
                        fontWeight="bold"
                        padding="5px 0"
                        variant="h4"
                        lineHeight="28px"
                      >
                        {paymentNotTime}
                      </Typography>
                    </Column>
                    <Column>
                      <Row>
                        <Typography color="black1" fontSize="14px" variant="h4" lineHeight="28px">
                          Reclamos por pago no oportuno
                        </Typography>
                      </Row>
                    </Column>
                  </Row>
                  <Separator />

                  <Row flexWrap="nowrap" padding="10px">
                    <Column display="flex" padding="0 15px 0 0">
                      <Typography
                        color="black1"
                        fontSize="18px"
                        fontWeight="bold"
                        padding="5px 0"
                        variant="h4"
                        lineHeight="28px"
                      >
                        {claimsIrregularity}
                      </Typography>
                    </Column>
                    <Column display="flex">
                      <Typography color="black1" fontSize="14px" padding="5px 0" variant="h4" lineHeight="28px">
                        Reclamos por irregularidad en el proceso de compra
                      </Typography>
                    </Column>
                  </Row>
                </Card>
              )}
              {!claimError && claimsIrregularity === 0 && paymentNotTime === 0 && (
                <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
                  <CardItem direction="column">
                    <NotFoundImg
                      alt="No se encontraron resultados"
                      margin="52px 0 0 0"
                      src={`${PUBLIC_URL}/assets/images/notFound.svg`}
                    />
                    <Typography
                      color="black1"
                      variant="h4"
                      fontWeight="bold"
                      fontSize="18px"
                      lineHeight="22px"
                      margin="10px 0 0 0"
                      tabIndex="0"
                    >
                      {ANNUAL_PURCHASE_PLAN_TABS.NO_INFO}
                    </Typography>
                    <Typography
                      color="black3"
                      variant="body2"
                      fontWeight="regular"
                      fontSize="14px"
                      lineHeight="18px"
                      margin="5px 0 52px 0"
                      tabIndex="0"
                      sx={{ maxWidth: '270px', textAlign: 'center' }}
                    >
                      El organismo comprador no ha recibido reclamos durante los últimos 12 meses.
                    </Typography>
                  </CardItem>
                </Card>
              )}
            </Row>
            <Row>
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                {GESTION_TITLE}
              </Typography>

              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                padding="11px 0 0 0"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                Fuente: Puntaje calculado en base al puntaje de las cinco dimensiones de Gestión de compras durante el
                mes de {averageBehaviorStringMonth || stringMonth} {averageBehaviorYear || year}.
              </Typography>
              {dimensions && !dimensionsError ? (
                <DimensionsChart
                  dimensions={dimensions}
                  yearsOptions={[]}
                  monthsOptions={[]}
                  yearsOptionsLoading={false}
                  monthsOptionsLoading={false}
                  dimensionsOptionsLoading={false}
                  onYearChange={() => {}}
                  onMonthChange={() => {}}
                  showFilters={false}
                  updatedAt={updatedAt}
                />
              ) : (
                <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
                  <CardItem direction="column">
                    <NotFoundImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/notFound.svg`} />
                    <Typography
                      color="black1"
                      variant="h4"
                      fontWeight="bold"
                      fontSize="18px"
                      lineHeight="22px"
                      margin="10px 0 0 0"
                      tabIndex="0"
                    >
                      No existe información
                    </Typography>
                  </CardItem>
                </Card>
              )}
            </Row>
            <Row padding="19px 0" flexDirection="column">
              <Typography color="black1" fontSize="18px" fontWeight="bold" variant="h4" lineHeight="22px" tabIndex="0">
                Demandas Tribunal de Contratación Pública
              </Typography>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                padding="11px 0 0 0"
                variant="h4"
                lineHeight="22px"
                tabIndex="0"
              >
                Acciones interpuestas ante el Tribunal de Contratación Pública
              </Typography>

              <Typography
                color="black3"
                display="inline"
                fontWeight="regular"
                fontSize="14px"
                padding="11px 0 0 0"
                variant="subtitle"
                lineHeight="18px"
                tabIndex="0"
              >
                Fuente: Tribunal de Contratación Pública (https://tribunaldecontratacionpublica.cl/)
              </Typography>
            </Row>
            {!publicProcurementCourtClaimsError && (
              <Row padding="19px 0 14px 0" maxWidth={isDownToMedium ? 'auto' : '66%'}>
                <Typography
                  fontWeight="regular"
                  variant="body1"
                  fontSize="14px"
                  color="black1"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Revisa las demandas que ha recibido este organismo comprador durante el periodo de{' '}
                  {gettingMonthAndYear(initialDate).month} {gettingMonthAndYear(initialDate).year} a{' '}
                  {gettingMonthAndYear(finalDate).month} {gettingMonthAndYear(finalDate).year}.
                </Typography>
              </Row>
            )}
            {!publicProcurementCourtClaimsError && totalDemands > 0 && (
              <Row padding="19px 0" maxWidth={isDownToMedium ? 'auto' : '89%'}>
                <Card padding="10px" display="flex" color="blank" enableBorder>
                  <Row padding="10px">
                    <Column lg={1} sm={1} xs={2} display="flex" justifyContent="center">
                      <Typography
                        color="black1"
                        fontSize="27px"
                        fontWeight="bold"
                        padding="5px 0"
                        variant="h4"
                        lineHeight="28px"
                      >
                        {totalDemands}
                      </Typography>
                    </Column>
                    <Column lg={9} sm={8} xs={7}>
                      <Row>
                        <Typography color="black1" fontSize="16px" fontWeight="bold" variant="h4" lineHeight="28px">
                          Demandas recibidas
                        </Typography>
                      </Row>
                      <Row>
                        <Typography fontSize="16px" variant="h4" lineHeight="22px">
                          Entre {gettingMonthAndYear(initialDate).month} {gettingMonthAndYear(initialDate).year} a{' '}
                          {gettingMonthAndYear(finalDate).month} {gettingMonthAndYear(finalDate).year}.
                        </Typography>
                      </Row>
                      <Row>
                        <Typography
                          color="black3"
                          display="inline"
                          fontWeight="regular"
                          fontSize="14px"
                          variant="subtitle"
                          lineHeight="28px"
                        >
                          Información actualizada al {formatDate(getCurrentDate, 'dd-MM-yyyy')}
                        </Typography>
                      </Row>
                    </Column>
                  </Row>
                </Card>
              </Row>
            )}
            {publicProcurementCourtClaimsError && (
              <Card
                display="flex"
                enableBorder
                color="blank"
                margin="43px 0 44px 0"
                alignContent="center"
                height={isTiny ? '139px' : '243px'}
              >
                <CardItem direction="row" padding="25px 16px">
                  <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
                  <Typography
                    color="black1"
                    variant="body1"
                    fontWeight="regular"
                    fontSize="16px"
                    lineHeight="21px"
                    margin="0 0 0 33px"
                    tabIndex="0"
                    sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
                  >
                    No ha sido posible obtener información de la institución. Por favor, te pedimos que lo vuelvas a
                    intentar dentro de unos minutos.
                  </Typography>
                </CardItem>
              </Card>
            )}
            {!publicProcurementCourtClaimsError && totalDemands === 0 && (
              <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
                <CardItem direction="column">
                  <NotFoundImg
                    alt="No se encontraron resultados"
                    margin="52px 0 0 0"
                    src={`${PUBLIC_URL}/assets/images/notFound.svg`}
                  />
                  <Typography
                    color="black1"
                    variant="h4"
                    fontWeight="bold"
                    fontSize="18px"
                    lineHeight="22px"
                    margin="10px 0 0 0"
                    tabIndex="0"
                  >
                    {ANNUAL_PURCHASE_PLAN_TABS.NO_INFO}
                  </Typography>
                  <Typography
                    color="black3"
                    variant="body2"
                    fontWeight="regular"
                    fontSize="14px"
                    lineHeight="18px"
                    margin="5px 0 52px 0"
                    tabIndex="0"
                    sx={{ maxWidth: '270px', textAlign: 'center' }}
                  >
                    El organismo comprador no ha recibido demandas durante los últimos 12 meses.
                  </Typography>
                </CardItem>
              </Card>
            )}
            <Container direction="row">
              <ScrollToTopButton />
            </Container>
          </Container>
        )}
        {loading && (
          <Card margin="0 0 50px 0" display="flex" color="blank">
            <CardItem direction="row" sx={{ margin: '97px auto' }}>
              <Spinner />
            </CardItem>
          </Card>
        )}
      </>
    </ContainerRef>
  )
}

export default ContainerToDownload
